import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchInventoryTransfers } from '../../services/inventoryTransferAPI';
import { getApiErrorMessage } from '../../services/errorUtils';
import type { InventoryTransferListItem } from '../../schemas/apiResponse.schemas';
import { NavbarPageActions } from '../layout/NavbarPageActions';
import { useInventoryTransferPicksRealtime } from '../../hooks/useTransferScanRealtime';
import type { TransferLineChangeBroadcast } from '@/shared/utils/inventoryTransferScan';
import { normalizeSku } from '../../utils/normalizeSku';
import '../orders/OrdersList.css';
import './InventoryTransfersList.css';

const SHOW_ALL_KEY = 'inventoryTransfersShowAll';

const KINK_STORE_WAREHOUSE = 'kink store warehouse';
const BRAND_RED = '#D72029';

const LOCATION_CHIP_COLORS = [
  '#1d4ed8',
  '#15803d',
  '#c2410c',
  '#7e22ce',
  '#0f766e',
  '#be185d',
  '#a16207',
  '#0369a1',
  '#b91c1c',
  '#4338ca',
] as const;

function isKinkStoreWarehouse(name: string): boolean {
  return name.trim().toLowerCase() === KINK_STORE_WAREHOUSE;
}

function locationChipColors(requests: readonly InventoryTransferListItem[]): ReadonlyMap<string, string> {
  const names = new Set<string>();
  for (const request of requests) {
    names.add(request.startLocationName);
    names.add(request.endLocationName);
  }
  const colors = new Map<string, string>();
  let paletteIndex = 0;
  for (const name of [...names].sort((a, b) => a.localeCompare(b))) {
    if (isKinkStoreWarehouse(name)) {
      colors.set(name, BRAND_RED);
      continue;
    }
    colors.set(name, LOCATION_CHIP_COLORS[paletteIndex % LOCATION_CHIP_COLORS.length]);
    paletteIndex += 1;
  }
  return colors;
}

function readShowAll(): boolean {
  return sessionStorage.getItem(SHOW_ALL_KEY) === 'true';
}

function formatMonthDay(month: string, day: string): string {
  return `${Number(month)}/${Number(day)}`;
}

function formatTransferDate(value: string): string {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) return formatMonthDay(dateOnly[2], dateOnly[3]);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    const prefix = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!prefix) return value;
    return formatMonthDay(prefix[2], prefix[3]);
  }
  return formatMonthDay(String(parsed.getMonth() + 1), String(parsed.getDate()));
}

function uniqueSkuCount(request: InventoryTransferListItem): number {
  return new Set(request.lines.map((line) => normalizeSku(line.sku)).filter((sku) => sku !== '')).size;
}

function scanPercent(request: InventoryTransferListItem): number {
  if (request.lines.length === 0) return 0;
  const scanned = new Set(request.scannedLineIds);
  const done = request.lines.filter((line) => scanned.has(line.id)).length;
  return Math.round((done / request.lines.length) * 100);
}

function InventoryTransfersList(): React.ReactElement {
  const [requests, setRequests] = useState<readonly InventoryTransferListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(readShowAll);
  const seenLineEventsRef = useRef(new Set<string>());
  const reloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showAllRef = useRef(showAll);
  showAllRef.current = showAll;

  const toggleShowAll = (): void => {
    setShowAll((prev) => {
      const next = !prev;
      sessionStorage.setItem(SHOW_ALL_KEY, String(next));
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;

    const load = async (): Promise<void> => {
      setLoading(true);
      setLoadError(null);
      try {
        const next = await fetchInventoryTransfers(showAll);
        if (!cancelled) setRequests(next);
      } catch (err: unknown) {
        if (!cancelled) {
          setRequests([]);
          setLoadError(getApiErrorMessage(err, 'Failed to load inventory transfers'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [showAll]);

  const applyRemoteLine = (change: TransferLineChangeBroadcast): void => {
    if (seenLineEventsRef.current.has(change.eventId)) return;
    seenLineEventsRef.current.add(change.eventId);
    const nextLine = change.line;
    setRequests((prev) => prev.map((request) => {
      if (request.id !== change.transferRequestId) return request;
      const lines = nextLine
        ? (request.lines.some((line) => line.id === change.lineId)
          ? request.lines.map((line) => (line.id === change.lineId
            ? { id: change.lineId, sku: nextLine.sku, quantity: nextLine.quantity }
            : line))
          : [...request.lines, { id: change.lineId, sku: nextLine.sku, quantity: nextLine.quantity }])
        : request.lines.filter((line) => line.id !== change.lineId);
      return { ...request, lines };
    }));
  };

  const scheduleReload = (): void => {
    if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
    reloadTimerRef.current = setTimeout(() => {
      void fetchInventoryTransfers(showAllRef.current).then((next) => {
        setRequests(next);
      }).catch(() => {
        // The next edit or toggle will load again.
      });
    }, 250);
  };

  useEffect(() => () => {
    if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
  }, []);

  useInventoryTransferPicksRealtime(applyRemoteLine, (event) => {
    if (seenLineEventsRef.current.has(event.eventId)) return;
    seenLineEventsRef.current.add(event.eventId);
    scheduleReload();
  }, (scan) => {
    setRequests((prev) => prev.map((request) => {
      if (request.id !== scan.transferRequestId) return request;
      if (request.scannedLineIds.includes(scan.lineId)) return request;
      return { ...request, scannedLineIds: [...request.scannedLineIds, scan.lineId] };
    }));
  });

  const siblingIds = useMemo(() => requests.map((request) => request.id), [requests]);
  const locationColors = useMemo(() => locationChipColors(requests), [requests]);

  return (
    <div className="orders-container inventory-transfers-page">
      <NavbarPageActions>
        <button
          type="button"
          className="orders-hide-issues-btn"
          aria-pressed={showAll}
          onClick={toggleShowAll}
        >
          {showAll ? 'Hide closed' : 'Show All Orders'}
        </button>
      </NavbarPageActions>
      {loading ? <p className="inventory-transfer-status">Loading...</p> : null}
      {loadError ? <p className="inventory-transfer-error">{loadError}</p> : null}
      {!loading && !loadError && requests.length === 0 ? (
        <p className="inventory-transfer-status">
          {showAll ? 'No inventory transfers' : 'No open inventory transfers'}
        </p>
      ) : null}
      {!loading && requests.length > 0 ? (
        <ul>
          {requests.map((request) => {
            const skuCount = uniqueSkuCount(request);
            const completePercent = scanPercent(request);
            return (
            <li key={request.id}>
              <Link
                className="order-box-container inventory-transfer-card"
                to={`/inventory-transfers/${request.id}`}
                state={{ fromPath: '/inventory-transfers', siblingOrderIds: siblingIds }}
              >
                <div className="inventory-transfer-card-top">
                  <div className="inventory-transfer-chip-main">
                    <div className="inventory-transfer-heading">
                      <div className="inventory-transfer-chip inventory-transfer-chip-number">
                        {request.transferNumber}
                      </div>
                      <div className="inventory-transfer-title">{request.name}</div>
                    </div>
                    <div
                      className="inventory-transfer-locations"
                      aria-label={`${request.startLocationName} to ${request.endLocationName}`}
                    >
                      <div
                        className="inventory-transfer-chip inventory-transfer-location-chip"
                        style={{ backgroundColor: locationColors.get(request.startLocationName) }}
                      >
                        {request.startLocationName}
                      </div>
                      <span className="inventory-transfer-route-arrow" aria-hidden="true">→</span>
                      <div
                        className="inventory-transfer-chip inventory-transfer-location-chip"
                        style={{ backgroundColor: locationColors.get(request.endLocationName) }}
                      >
                        {request.endLocationName}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="inventory-transfer-details">
                  <div className="inventory-transfer-details-main">
                    <span className="inventory-transfer-details-title">Details</span>
                    <span className="inventory-transfer-field">
                      <span className="inventory-transfer-field-label">Requestor:</span>
                      <span className="inventory-transfer-field-value">{request.requestorName}</span>
                    </span>
                    <span className="inventory-transfer-field">
                      <span className="inventory-transfer-field-label">Submitted:</span>
                      <span className="inventory-transfer-field-value">{formatTransferDate(request.createdAt)}</span>
                    </span>
                    <span className="inventory-transfer-field">
                      <span className="inventory-transfer-field-label">Pickup:</span>
                      <span className="inventory-transfer-field-value">{formatTransferDate(request.requestedDueDate)}</span>
                    </span>
                  </div>
                  <div className="inventory-transfer-metrics">
                    <div className="inventory-transfer-metric-stack">
                      <span className="inventory-transfer-metric-label inventory-transfer-metric-label-skus">SKUs</span>
                      <div className="inventory-transfer-metric" aria-label={`${skuCount} SKUs`}>
                        {skuCount}
                      </div>
                    </div>
                    <div className="inventory-transfer-metric-stack">
                      <span className="inventory-transfer-metric-label">Progress</span>
                      <div className="inventory-transfer-metric" aria-label={`${completePercent} percent progress`}>
                        {completePercent}%
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

export default InventoryTransfersList;
